import * as Sentry from "@sentry/react";
import { FrontendErrorCategory, type SentryLevel, type SentryEventContext } from "./sentry.types";

interface SentryUser {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  tenant_id?: string;
}

export class SentryService {
  static setUser(user: SentryUser): void {
    Sentry.setUser({ id: user.id, email: user.email, username: user.name });

    Sentry.setTag("user_role", user.role ?? "unknown");
    Sentry.setTag("is_authenticated", "true");
    if (user.tenant_id) {
      Sentry.setTag("tenant_id", user.tenant_id);
    }
  }

  static clearUser(): void {
    Sentry.setUser(null);
    Sentry.setTag("is_authenticated", "false");
    Sentry.setTag("user_role", undefined);
    Sentry.setTag("tenant_id", undefined);
  }

  static shouldCapture(statusCode?: number): boolean {
    // No status (e.g. network failure) is unexpected → capture.
    if (statusCode === undefined) return true;
    // 4xx are expected client errors; only report server-side failures.
    return statusCode >= 500;
  }

  static captureException(error: unknown, context: SentryEventContext): void {
    Sentry.withScope((scope) => {
      scope.setLevel(context.level);

      scope.setTags({
        error_category: context.errorCategory,
        error_type: context.errorType ?? "Unknown",
      });

      if (context.route) scope.setTag("route", context.route);
      if (context.component) scope.setTag("component", context.component);
      if (context.httpMethod) scope.setTag("http.method", context.httpMethod);
      if (context.httpUrl) scope.setTag("http_url", context.httpUrl);
      if (context.httpStatusCode !== undefined) {
        scope.setTag("http_status_code", String(context.httpStatusCode));
      }
      if (context.correlationId) {
        scope.setTag("correlation_id", context.correlationId);
      }
      if (context.apiType) scope.setTag("api_type", context.apiType);

      Sentry.captureException(error);
    });
  }

  static classifyError(error: unknown): FrontendErrorCategory {
    if (error instanceof Error) {
      if (error.name === "ChunkLoadError" || error.message.includes("Loading chunk")) {
        return FrontendErrorCategory.ChunkLoadError;
      }

      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError") ||
        error.message.includes("Network request failed") ||
        (error.name === "TypeError" && error.message.includes("fetch"))
      ) {
        return FrontendErrorCategory.NetworkError;
      }
    }

    return FrontendErrorCategory.Unknown;
  }

  static resolveLevel(
    statusCode: number | undefined,
    errorCategory: FrontendErrorCategory
  ): SentryLevel {
    if (errorCategory === FrontendErrorCategory.ChunkLoadError) return "fatal";

    if (errorCategory === FrontendErrorCategory.NetworkError && statusCode === undefined) {
      return "fatal";
    }

    if (statusCode !== undefined && statusCode >= 500) return "error";

    if (errorCategory === FrontendErrorCategory.RenderError) return "error";

    return "warning";
  }

  static extractModule(pathname: string): string {
    if (!pathname || pathname === "/") return "dashboard";
    const segment = pathname.replace(/^\//, "").split("/")[0];
    return segment || "unknown";
  }
}
